import { supabase } from '@/lib/supabase';
import { r2Upload, r2Url } from '@/lib/r2';
import type { HwinfoMetrics } from '@/lib/hwinfo';

/**
 * Stress test results captured during assembly, one row per unit.
 *
 * Keyed on (assembly_order_id, unit_number) like assembly_order_benchmarks, so
 * a result inherits the unit's serial through assembly_order_serials without
 * any new join.
 */
export interface StressTest {
  id: string;
  assembly_order_id: string;
  unit_number: number;
  technician_id: string | null;
  tool: string;
  file_path: string | null;
  file_name: string | null;
  file_size_bytes: number | null;
  started_at: string | null;
  duration_seconds: number | null;
  sample_count: number | null;
  cpu_temp_max: number | null;
  cpu_temp_avg: number | null;
  gpu_temp_max: number | null;
  gpu_temp_avg: number | null;
  gpu_hotspot_max: number | null;
  cpu_clock_min: number | null;
  cpu_clock_max: number | null;
  gpu_clock_min: number | null;
  gpu_clock_max: number | null;
  gpu_power_max: number | null;
  fan_rpm_max: number | null;
  thermal_throttled: boolean | null;
  metrics: Record<string, unknown>;
  notes: string | null;
  created_at: string;
  created_by: string | null;
}

const SELECT_COLS =
  'id, assembly_order_id, unit_number, technician_id, tool, file_path, file_name, ' +
  'file_size_bytes, started_at, duration_seconds, sample_count, cpu_temp_max, cpu_temp_avg, ' +
  'gpu_temp_max, gpu_temp_avg, gpu_hotspot_max, cpu_clock_min, cpu_clock_max, gpu_clock_min, ' +
  'gpu_clock_max, gpu_power_max, fan_rpm_max, thermal_throttled, metrics, notes, created_at, created_by';

/** Every stress result recorded against one assembly order, oldest unit first. */
export async function fetchStressTests(assemblyOrderId: string): Promise<StressTest[]> {
  const { data, error } = await supabase
    .from('assembly_order_stress_tests')
    .select(SELECT_COLS)
    .eq('assembly_order_id', assemblyOrderId)
    .order('unit_number', { ascending: true });
  if (error) throw error;
  return (data ?? []) as unknown as StressTest[];
}

/** Public URL for a stored log, or null when the row carries no file. */
export function stressTestFileUrl(t: Pick<StressTest, 'file_path'>): string | null {
  return t.file_path ? r2Url(t.file_path) : null;
}

/**
 * Object key for one unit's log.
 *
 * Namespaced by assembly order and unit so the bucket stays navigable by eye,
 * and stamped with the upload time so re-running a stress test on the same unit
 * does not overwrite the earlier evidence in R2 — the database row is replaced,
 * but the superseded file is still there to be found.
 */
function logKey(assemblyOrderId: string, unitNumber: number, fileName: string): string {
  const safe = fileName.replace(/[^A-Za-z0-9._-]/g, '_').slice(-80);
  return `stress-tests/${assemblyOrderId}/unit-${unitNumber}-${Date.now()}-${safe}`;
}

export interface SaveStressTestArgs {
  assemblyOrderId: string;
  unitNumber: number;
  technicianId?: string | null;
  createdBy?: string | null;
  /** The raw HWiNFO log. Optional: numbers may be recorded without keeping it. */
  file?: File | null;
  metrics: HwinfoMetrics;
  notes?: string | null;
}

/**
 * Upload the log to R2 (when one was given) and record the extracted metrics.
 *
 * The file goes up first on purpose. If the upload fails the whole save fails
 * and nothing is written, rather than leaving a row that points at an object
 * which is not there.
 */
export async function saveStressTest(args: SaveStressTestArgs): Promise<StressTest> {
  const { assemblyOrderId, unitNumber, technicianId, createdBy, file, metrics, notes } = args;

  let filePath: string | null = null;
  if (file) {
    const key = logKey(assemblyOrderId, unitNumber, file.name);
    // HWiNFO writes .csv; send a concrete type so the object is readable in a
    // browser rather than downloading as an opaque blob.
    await r2Upload(key, file, file.type || 'text/csv');
    filePath = key;
  }

  const { data, error } = await supabase
    .from('assembly_order_stress_tests')
    .upsert({
      assembly_order_id: assemblyOrderId,
      unit_number: unitNumber,
      technician_id: technicianId ?? null,
      tool: 'hwinfo',
      file_path: filePath,
      file_name: file?.name ?? null,
      file_size_bytes: file?.size ?? null,
      // HWiNFO logs wall-clock time with no offset, and parseHwinfoText keeps
      // it that way. Sent bare, Postgres read it as UTC, storing every run 1-2h
      // late. new Date() on an offset-less ISO string reads it as local time,
      // which is the workshop's: logs are uploaded from workshop machines.
      started_at: metrics.startedAt ? new Date(metrics.startedAt).toISOString() : null,
      duration_seconds: metrics.durationSeconds,
      sample_count: metrics.sampleCount,
      cpu_temp_max: metrics.cpuTempMax,
      cpu_temp_avg: metrics.cpuTempAvg,
      gpu_temp_max: metrics.gpuTempMax,
      gpu_temp_avg: metrics.gpuTempAvg,
      gpu_hotspot_max: metrics.gpuHotspotMax,
      cpu_clock_min: metrics.cpuClockMin,
      cpu_clock_max: metrics.cpuClockMax,
      gpu_clock_min: metrics.gpuClockMin,
      gpu_clock_max: metrics.gpuClockMax,
      gpu_power_max: metrics.gpuPowerMax,
      fan_rpm_max: metrics.fanRpmMax,
      thermal_throttled: metrics.thermalThrottled,
      // Which sensor each figure came from, and what this board did not log.
      // Kept so an odd number can be traced to its column without the file.
      metrics: {
        matchedSensors: metrics.matchedSensors,
        unmatched: metrics.unmatched,
        otherThrottleReasons: metrics.otherThrottleReasons,
        dateAmbiguous: metrics.dateAmbiguous,
        // Marks started_at as true UTC. Rows without it were stored as
        // local time and corrected by migration 20261030120000.
        startedAtUtc: true,
      },
      notes: notes ?? null,
      created_by: createdBy ?? null,
    }, { onConflict: 'assembly_order_id,unit_number,tool' })
    .select(SELECT_COLS)
    .single();

  if (error) throw error;
  return data as unknown as StressTest;
}

/** Human-readable run length, e.g. "12m 30s". */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds < 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}
